import type { Dayjs } from 'dayjs'

import dayjs from './dayjs'
import { capitalize } from './string-utils'

// dateEarlyDefault is declared in index.html
declare global {
  interface Window {
    dateEarlyDefault: string
  }
}

export type DayInMonth = {
  inMonth: boolean
  checked: boolean
  dayjs: Dayjs
}

// Get the default early limit from .env if defined
// else the current time minus 1 year
// Returns a dayjs object
export function getDefaultEarlyLimit(): dayjs.Dayjs {
  return window.dateEarlyDefault ? dayjs(window.dateEarlyDefault) : dayjs().startOf('year')
}

export function getDayOrderedWeekDaysCapitalized(): string[] {
  const weekDays = dayjs.weekdaysMin().map((day) =>  capitalize(day))
  const offset = dayjs.localeData().firstDayOfWeek()
  const orderedWeekdays = []
  for (let i = 0; i < weekDays.length; i++) {
    orderedWeekdays.push(weekDays[(i + offset) % weekDays.length])
  }
  return orderedWeekdays
}

export function getMonthsCapitalized(): string[] {
  return dayjs.months().map((month) => capitalize(month))
}

export function getVisibleYears(): number[] {
  const baseYear = dayjs().year()
  const visibleYears: number[] = []
  for (let i = baseYear + 5; i >= baseYear - 30; i--) {
    visibleYears.push(i)
  }
  return visibleYears
}

export function getVisibleWeeks(currentDay: Dayjs, baseTime?: Dayjs): DayInMonth[][] {
  if (! baseTime) {
    baseTime = dayjs()
  }

  const monthDays = baseTime.daysInMonth()
  const days : DayInMonth[] = []

  const firstDay = baseTime.date(1).weekday()
  for (let i = firstDay; i > 0; i--) {
    const day: DayInMonth = {
      inMonth: false,
      checked: false,
      dayjs: baseTime.date(1).subtract(i, 'day')
    }
    days.push(day)
  }

  for (let i = 1; i <= monthDays; i++) {
    const daysObject = dayjs(baseTime.date(i))
    const day = {
      inMonth: true,
      checked: false,
      dayjs: daysObject
    }

    if (daysObject.isSame(currentDay)) {
      day.checked = true
    }
    days.push(day)
  }
  
  // Add padding days at the end (42 days = 6 weeks)
  const missingDays = 42 - days.length
  for (let i = 1; i <= missingDays; i++) {
    days.push({
      inMonth: false,
      checked: false,
      dayjs: baseTime.date(monthDays).add(i, 'day'),
    })
  }
  
  const weeks = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }
  return weeks
}

export const dateFormat = 'DD/MM/YYYY'
export const timeFormat = 'HH:mm:ss'
export const dateTimeFormat = `${dateFormat} ${timeFormat}`
